import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import MobileBottomNav from "@/components/MobileBottomNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, ShieldCheck, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Admin() {
  const { user, refreshMe } = useAuth();
  const [users, setUsers] = useState([]);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((t) => t + 1);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));
    refreshMe().catch(() => {});
  }, [tick]);

  const tenant = user?.tenant;

  return (
    <div className="min-h-screen bg-black text-zinc-100 flex flex-col pb-14 md:pb-0">
      <AppHeader active="admin" />

      <div className="px-4 py-3 border-b border-zinc-900 bg-zinc-950 flex items-center justify-between">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-cyan-400">Organization Administration</div>
          <h1 className="font-display text-xl mt-1">{tenant?.name || "Control Center"}</h1>
        </div>
        {tenant && <SubscriptionBanner tenant={tenant} />}
      </div>

      <div className="flex-1 p-4 overflow-auto">
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="rounded-none bg-zinc-950 border border-zinc-900 p-0 h-9">
            <TabsTrigger data-testid="tab-users" value="users" className="rounded-none px-4 h-9 text-[11px] uppercase tracking-widest data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-400">Users</TabsTrigger>
            <TabsTrigger data-testid="tab-subscription" value="subscription" className="rounded-none px-4 h-9 text-[11px] uppercase tracking-widest data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-400">Subscription</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-4"><UsersTab users={users} onChange={refresh} /></TabsContent>
          <TabsContent value="subscription" className="mt-4"><SubscriptionTab tenant={tenant} /></TabsContent>
        </Tabs>
      </div>
      <MobileBottomNav active="admin" />
    </div>
  );
}

/* -------------------- Subscription Banner (header) -------------------- */
function SubscriptionBanner({ tenant }) {
  const status = tenant.plan_status;
  const days = tenant.days_remaining;
  if (status === "expired") {
    return (
      <div className="mono text-[11px] flex items-center gap-2 border border-red-500/60 bg-red-950/40 text-red-400 px-3 py-1.5">
        <AlertTriangle size={13} />
        <span className="uppercase tracking-widest">Subscription Expired</span>
      </div>
    );
  }
  if (status === "grace") {
    return (
      <div className="mono text-[11px] flex items-center gap-2 border border-amber-500/60 bg-amber-950/30 text-amber-400 px-3 py-1.5">
        <AlertTriangle size={13} />
        <span className="uppercase tracking-widest">Grace Period · {Math.abs(days ?? 0)}d after expiry</span>
      </div>
    );
  }
  return (
    <div className="mono text-[11px] flex items-center gap-2 border border-emerald-500/40 bg-emerald-950/20 text-emerald-400 px-3 py-1.5">
      <ShieldCheck size={13} />
      <span className="uppercase tracking-widest">Active · {days} days left</span>
    </div>
  );
}

/* -------------------- Subscription Tab -------------------- */
function SubscriptionTab({ tenant }) {
  if (!tenant) {
    return <div className="mono text-[11px] text-zinc-500">Loading...</div>;
  }
  const startStr = tenant.plan_start ? tenant.plan_start.split("T")[0] : "—";
  const endStr = tenant.plan_end ? tenant.plan_end.split("T")[0] : "—";
  const status = tenant.plan_status;
  const days = tenant.days_remaining;

  const barColor = status === "expired" ? "bg-red-500" : status === "grace" ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="border border-zinc-900 bg-zinc-950 p-5">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Organization</div>
        <div className="font-display text-2xl mt-1" data-testid="sub-tenant-name">{tenant.name}</div>
        <div className="mono text-[11px] text-zinc-500 mt-1">/{tenant.slug}</div>

        <div className="mt-5 grid grid-cols-2 gap-4 border-t border-zinc-900 pt-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Plan Start</div>
            <div className="mono text-sm mt-1" data-testid="sub-start">{startStr}</div>
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Plan End</div>
            <div className="mono text-sm mt-1" data-testid="sub-end">{endStr}</div>
          </div>
        </div>
      </div>

      <div className="border border-zinc-900 bg-zinc-950 p-5">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Subscription Status</div>
        <div className="flex items-baseline gap-2 mt-1">
          <div className={`font-display text-3xl ${status === "expired" ? "text-red-400" : status === "grace" ? "text-amber-400" : "text-emerald-400"}`}
               data-testid="sub-status">
            {(status || "unknown").toUpperCase()}
          </div>
        </div>

        <div className="mt-3 mono text-[11px] text-zinc-500 flex items-center gap-2">
          <Clock size={12} />
          {days >= 0 ? `${days} days remaining` : `Expired ${Math.abs(days)} days ago`}
        </div>

        <div className="mt-4 h-2 bg-zinc-900 relative overflow-hidden" data-testid="sub-progress">
          <div className={`absolute inset-y-0 left-0 ${barColor}`} style={{ width: `${Math.max(0, Math.min(100, (days ?? 0) / 365 * 100))}%` }} />
        </div>

        <div className="mt-5 border-t border-zinc-900 pt-4 mono text-[10px] text-zinc-500 leading-relaxed">
          {status === "expired" ? (
            <>Your subscription has expired. Contact your system provider to renew and restore write access.</>
          ) : status === "grace" ? (
            <>Your subscription expired but you are still in the {Math.abs(days ?? 0)}-day grace period. Renew soon to avoid interruption.</>
          ) : (
            <>Your 1-year plan is active. Renewal handled by your system provider — no user action required.</>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Users Tab -------------------- */
function UsersTab({ users, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", role: "user" });

  const submit = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ username: "", password: "", role: "user" });
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("User deleted"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="mono text-[11px] text-zinc-500 uppercase tracking-widest">Console Users</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black h-8 text-[11px] uppercase tracking-widest">
              <Plus size={14} className="mr-1" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-zinc-950 border-zinc-800 text-zinc-100">
            <DialogHeader>
              <DialogTitle className="font-display">Create User</DialogTitle>
              <DialogDescription className="mono text-[10px] text-zinc-500">Add a viewer or admin to this organization.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Username</Label>
                <Input data-testid="user-username" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
              <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Password</Label>
                <Input data-testid="user-password" type="password" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role" className="rounded-none bg-black border-zinc-800 mono mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                    <SelectItem value="user">user (view only)</SelectItem>
                    <SelectItem value="admin">admin</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <DialogFooter>
              <Button data-testid="user-save" disabled={!form.username || !form.password} onClick={submit} className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-900">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-zinc-950 border-b border-zinc-900 text-zinc-500 text-[10px] uppercase tracking-widest">
              <th className="text-left p-2 font-normal">Username</th>
              <th className="text-left p-2 font-normal">Role</th>
              <th className="text-left p-2 font-normal">Created</th>
              <th className="text-right p-2 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-zinc-900 hover:bg-zinc-950/50">
                <td className="p-2 mono">{u.username}</td>
                <td className="p-2 mono">
                  <span className={`px-1.5 py-0.5 border text-[10px] uppercase tracking-widest ${u.role === "admin" ? "border-cyan-500/60 text-cyan-400" : "border-zinc-700 text-zinc-400"}`}>
                    {u.role}
                  </span>
                </td>
                <td className="p-2 mono text-zinc-500">{u.created_at?.split("T")[0]}</td>
                <td className="p-2 text-right">
                  <Button data-testid={`user-delete-${u.username}`} variant="ghost" onClick={() => del(u.id)} className="rounded-none h-7 text-red-400 hover:bg-red-950/40"><Trash2 size={13} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
